// src/utils/anonymousSwipeStorage.js
// Utility for storing anonymous swipes in localStorage before user registration
import { getAnonymousUserId } from './anonymousUser';
import { getBackendUrl } from './apiUrl';

const STORAGE_KEY = 'rxrooster_anonymous_swipes';
const EXPIRY_HOURS = 24; // Swipes expire after 24 hours

/**
 * Get all stored anonymous swipes
 * @returns {Array} Array of swipe objects
 */
export const getAnonymousSwipes = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const data = JSON.parse(stored);
    const now = Date.now();

    // Filter out expired swipes
    const validSwipes = data.swipes.filter(swipe => {
      const swipeAge = now - swipe.timestamp;
      const maxAge = EXPIRY_HOURS * 60 * 60 * 1000;
      return swipeAge < maxAge;
    });

    // Update storage if we filtered anything out
    if (validSwipes.length !== data.swipes.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ swipes: validSwipes }));
    }

    return validSwipes;
  } catch (error) {
    console.error('Error reading anonymous swipes:', error);
    return [];
  }
};

/**
 * Store an anonymous swipe
 * @param {Object} swipe - Swipe data
 * @param {string} swipe.userType - 'provider' or 'group'
 * @param {string} swipe.direction - 'left' or 'right'
 * @param {string} swipe.targetId - ID of the listing or provider profile
 * @param {Object} swipe.targetData - Full data of the swiped item (for display later)
 * @param {Object} [swipe.preferences] - The SWIPER's own stated preferences,
 *   and only if a step actually collected them from this viewer (see
 *   AnonymousSwipeExperience's quick-settings screen). Omit it otherwise. It is
 *   persisted server-side on AnonymousSwipe and replayed to recruiters as the
 *   provider's stated preference, so a placeholder here becomes a false claim
 *   about a real person. Never default it, and never fill it from the swiped
 *   entity's own fields.
 */
export const storeAnonymousSwipe = async (swipe) => {
  try {
    const anonymousUserId = getAnonymousUserId();
    const swipes = getAnonymousSwipes();

    const newSwipe = {
      ...swipe,
      anonymousUserId,
      timestamp: Date.now(),
      id: `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    swipes.push(newSwipe);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ swipes }));

    // Send to backend. `synced` reports whether the server actually accepted
    // the swipe, so callers can avoid telling the user something happened when
    // it did not. The POST stays best-effort (the local copy survives for
    // flushAnonymousSwipesToBackend to retry), but the failure is no longer
    // invisible to the caller.
    let synced = false;
    try {
      const targetType = swipe.userType === 'provider' ? 'listing' : 'provider';
      const backendUrl = getBackendUrl();
      const url = `${backendUrl}/swipes/anonymous`;

      console.log('[AnonymousSwipe] Sending swipe to backend:', {
        url,
        anonymousUserId,
        userType: swipe.userType,
        targetType,
        targetId: swipe.targetId,
        direction: swipe.direction
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
          // Note: Removed X-Anonymous-User-Id header to avoid CORS issues
          // The anonymousUserId is in the request body
        },
        body: JSON.stringify({
          anonymousUserId,
          userType: swipe.userType,
          targetType,
          targetId: swipe.targetId,
          direction: swipe.direction,
          preferences: swipe.preferences,
          contactRequest: swipe.contactRequest,
          targetData: swipe.targetData
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AnonymousSwipe] Backend error:', response.status, errorText);
      } else {
        const result = await response.json();
        synced = true;
        console.log('[AnonymousSwipe] Backend success:', result);
      }
    } catch (backendError) {
      console.error('[AnonymousSwipe] Error sending swipe to backend:', backendError);
      // Don't fail if backend is unavailable - swipe is still stored locally
    }

    return { ...newSwipe, synced };
  } catch (error) {
    console.error('Error storing anonymous swipe:', error);
    return null;
  }
};

/**
 * Re-send locally-stored anonymous swipes to the backend.
 *
 * The capture-time POST in storeAnonymousSwipe is best-effort and can silently
 * fail (network / CORS / transient 5xx), leaving NO server-side AnonymousSwipe
 * for the auth-time reconcile to convert — so the liked listing never reaches the
 * pipeline on any login. Calling this at auth (before /swipes/claim-anonymous)
 * makes capture durable: it upserts the server record from the surviving local
 * copy. The backend upsert is idempotent, so re-sending an already-stored swipe
 * is a no-op.
 *
 * Best-effort and non-blocking: never throws to the caller.
 */
export const flushAnonymousSwipesToBackend = async () => {
  try {
    const swipes = getAnonymousSwipes();
    if (!swipes.length) return;
    const url = `${getBackendUrl()}/swipes/anonymous`;

    await Promise.all(
      swipes.map(async (swipe) => {
        try {
          const targetType = swipe.userType === 'provider' ? 'listing' : 'provider';
          await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              anonymousUserId: swipe.anonymousUserId,
              userType: swipe.userType,
              targetType,
              targetId: swipe.targetId,
              direction: swipe.direction,
              preferences: swipe.preferences,
              contactRequest: swipe.contactRequest,
              targetData: swipe.targetData,
            }),
          });
        } catch (err) {
          // Leave the local copy in place for a later flush attempt.
          console.warn('[AnonymousSwipe] flush retry failed for a swipe:', err?.message);
        }
      }),
    );
  } catch (err) {
    console.warn('[AnonymousSwipe] flushAnonymousSwipesToBackend failed:', err?.message);
  }
};

/**
 * Get count of right swipes (interested)
 * @param {string} userType - 'provider' or 'group'
 * @returns {number}
 */
export const getInterestedCount = (userType) => {
  const swipes = getAnonymousSwipes();
  return swipes.filter(s => s.userType === userType && s.direction === 'right').length;
};

/**
 * Clear all anonymous swipes (called after successful registration)
 */
export const clearAnonymousSwipes = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing anonymous swipes:', error);
  }
};

/**
 * Get anonymous swipes for a specific user type
 * @param {string} userType - 'provider' or 'group'
 * @returns {Array}
 */
export const getAnonymousSwipesByType = (userType) => {
  const swipes = getAnonymousSwipes();
  return swipes.filter(s => s.userType === userType);
};

/**
 * Check if user has any right swipes
 * @param {string} userType - 'provider' or 'group'
 * @returns {boolean}
 */
export const hasInterestedSwipes = (userType) => {
  return getInterestedCount(userType) > 0;
};
